import {createSelector} from 'reselect'
import {getSavedCardInfo, getStatesByCountry} from '../../selectors'
import {paymentMethod, priceType, purchaseStep, purchaseStrategyType} from '../../constants'
import {getEditable, getHolderName, isStripeFieldsValid} from '../creditCard/selectors'

const getPurchaseState = state => state.licenses.purchase

export const getProcessId = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.processId
)

export const isCompleted = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.completed
)

export const getPurchaseStepNumber = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.purchaseStepNumber
)

export const getPurchaseStrategyType = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.purchaseStrategyType
)

export const getCompanyInfo = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.companyInfo
)

export const getContactFirstName = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.contactFirstName
)

export const getContactLastName = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.contactLastName
)

export const getCompanyContactMail = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.contactMail
)

export const getHostingZone = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.newSegment
)

export const isHostingZoneChanged = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.segment !== purchaseState.newSegment
)

export const getLockedZone = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.lockedSegment
)

export const getContext = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.newContext
)

export const getInitialContext = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.context
)

export const isContextChanged = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.context !== purchaseState.newContext
)

export const isContextInUse = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.newContextInUse
)

export const isContextInvalid = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.newContextInvalid
)

export const getContextCheckingStatus = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.contextCheckingStatus
)

export const getPromoEditorMode = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.promoEditorMode
)

export const getPromoCode = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.promoCode
)

export const isPromoInvalid = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.promoInvalid
)

export const getBasePrice = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.basePrice
)

export const getTotalPrice = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.totalPrice
)

export const getOptionalPrices = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.optionalPrices.filter((price) =>
        price.amount !== null && price.description !== null
    )
)

export const getPaymentMethod = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.paymentMethod
)

export const areNewAgreementsNeeded = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.newAgreementsNeeded
)

export const areAgreementsConfirmed = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.agreementsConfirmed
)

export const isTransferHostingZoneProcessing = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.transferHostingZoneProcessing
)

export const hasAllCompanyInfo = createSelector(
    getCompanyInfo,
    getStatesByCountry,
    (companyInfo, statesByCountry) => {
        const hasStateIfNeeded = !statesByCountry[companyInfo.country] || !!statesByCountry[companyInfo.country] && companyInfo.state
        return [
                   companyInfo.name,
                   companyInfo.address,
                   companyInfo.city,
                   companyInfo.zip,
                   companyInfo.country
               ].every((elem) => !!elem) && hasStateIfNeeded
    }
)

export const getDocumentUrls = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.documentUrls
)

export const isContextCorrect = createSelector(
    isContextInUse,
    isContextInvalid,
    (contextInUse, contextInvalid) => !contextInUse && !contextInvalid
)

export const canGetFreeLicense = createSelector(
    isContextCorrect,
    areNewAgreementsNeeded,
    areAgreementsConfirmed,
    (contextCorrect, newAgreementsNeeded, agreementsConfirmed) => contextCorrect && (agreementsConfirmed || !newAgreementsNeeded)
)

export const canPayByWireTransfer = createSelector(
    getPurchaseState,
    (purchaseState) => purchaseState.canPayByWireTransfer
)

export const canPayByCheck = createSelector(
    getPurchaseState,
    (purchaseState) => purchaseState.canPayByCheck
)

export const isCanPayByCurrentPaymentMethod = createSelector(
    getPaymentMethod,
    canPayByWireTransfer,
    canPayByCheck,
    (method, canPayByWireTransfer, canPayByCheck) => method === paymentMethod.CC
                                                     || method === paymentMethod.WIRE && canPayByWireTransfer
                                                     || method === paymentMethod.CHECK && canPayByCheck
)

export const haveSavedCard = createSelector(
    getSavedCardInfo,
    (cardInfo) => cardInfo !== null
)

export const canGetLicense = createSelector(
    areNewAgreementsNeeded,
    areAgreementsConfirmed,
    isCanPayByCurrentPaymentMethod,
    getPaymentMethod,
    getEditable,
    haveSavedCard,
    isStripeFieldsValid,
    getHolderName,
    (newAgreementsNeeded, agreementsConfirmed, canPayByCurrentPaymentMethod, method, cardEditable, haveSavedCard, stripeFieldsValid, holderName) => {
        return (agreementsConfirmed || !newAgreementsNeeded) && canPayByCurrentPaymentMethod
               && (method !== paymentMethod.CC || !cardEditable && haveSavedCard || stripeFieldsValid && !!holderName)
    }
)

export const getPurchaseSteps = createSelector(
    getPurchaseStrategyType,
    (strategyType) => {
        let steps = []
        if (strategyType === purchaseStrategyType.FIRST_PURCHASE) {
            steps = [purchaseStep.COMPANY_INFO, purchaseStep.ACCOUNT_INFO, purchaseStep.PAYMENT]
        } else if (strategyType === purchaseStrategyType.FIRST_FREE) {
            steps = [purchaseStep.COMPANY_INFO, purchaseStep.ACCOUNT_INFO]
        } else if (strategyType === purchaseStrategyType.FREE) {
            steps = [purchaseStep.SWITCH_TO_FREE]
        } else if (strategyType === purchaseStrategyType.PURCHASE || strategyType === purchaseStrategyType.ADD_USERS) {
            steps = [purchaseStep.PAYMENT]
        }
        steps.push(purchaseStep.SUCCESS)
        return steps
    }
)

export const idLoading = createSelector(
    getPurchaseState,
    (purchaseState) => purchaseState.loading
)

export const getVatNumber = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.vatNumber
)

export const getPoNumber = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.poNumber
)

export const getNewProductUrl = createSelector(
    getPurchaseState,
    purchaseState => purchaseState.newProductUrl
)

export const getPaymentMethodFeeAmount = createSelector(
    getPurchaseState,
    purchaseState => {
        const optionalPrices = purchaseState.optionalPrices
        if (optionalPrices.length > 0) {
            const feePrice = optionalPrices.find((value) => {
                return value.type === priceType.PAYMENT_METHOD_FEE
            })

            if (feePrice && feePrice.amount !== null) {
                return feePrice.amount
            }
        }

        return 0
    }
)


// WEBPACK FOOTER //
// ./web/js/administration/licenses/components/purchaseLightBox/selectors.js